import { mapActions, mapGetters } from 'vuex';
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue';
import precoVisualizacaoMixin from './precoVisualizacao.mixin';

/**
 * Lógica compartilhada entre os modais "Adicionar Produto":
 *  - modalAdicionarItens.vue (novo pedido)
 *  - modalAdicionarEditarItensPedidoExistente.vue (pedido existente)
 *
 * Cobre: busca por marca/categoria, busca por código de referência,
 * processamento em blocos (paginação de 12 em 12 + "Carregar mais") e
 * o reset/limpeza de estado.
 *
 * As diferenças reais entre os dois fluxos (origem dos dados de capa do
 * pedido, simulação de imposto, montagem do item para a tabela e o
 * comportamento do resetModal) permanecem em cada componente. O único ponto
 * de variação exigido por esta lógica comum é o método `getTabelaPrecoId()`,
 * que cada componente deve implementar apontando para o seu store.
 */
export default {
  mixins: [precoVisualizacaoMixin],
  data() {
    return {
      isLoadingAllData: false,
      referenciasPendentes: [],
      tamanhoBlocoReferencias: 12,
      selected_cod_marca: null,
      selected_categoria: null,
      selected_referencia: null,
      selected_embalagem: null,
      busca_referencia: '',
      valorUnitario: null,
      categoriasOptions: [],
      referenciasOptions: [],
      embalagensOptions: [],
      itensTabela: [],
      novosItensAdicionados: false,
      gradeAtual: [],
      arrayEmbalagens: [],
      isCalculandoImposto: false,
      tipoBusca: 'marca-categoria',
      codigo_referencia_direto: '',
      opcoesTypeBusca: [
        { text: 'Buscar por Marca e Categoria', value: 'marca-categoria' },
        { text: 'Buscar por Código de Referência', value: 'codigo-referencia' },
      ],
      // Busca simples por filtros de atributo (listagem em linhas)
      filtroTexto: '',
      filtroLingua: null,
      filtroSegmento: null,
      filtroMarca: null,
      filtroNivel: null,
      filtroColecao: null,
      filtrosOptions: {
        lingua: [], segmento: [], niveleducacional: [], colecao: [], marcas: [],
      },
      isLoadingFiltros: false,
      buscaFiltrosRealizada: false,
    };
  },
  mounted() {
    this.clearjsonTelaNovaPedido();
  },
  beforeDestroy() {
    this._desobservarSentinelaReferencias();
  },
  watch: {
    // Liga/desliga o scroll infinito conforme existem (ou não) referências pendentes.
    temMaisReferencias(temMais) {
      if (temMais) {
        this.$nextTick(() => this._observarSentinelaReferencias());
      } else {
        this._desobservarSentinelaReferencias();
      }
    },
  },
  computed: {
    totalItensNoPedido() {
      const storeCount = (this.getlistarTabelaItens || [])
        .reduce((sum, item) => sum + (item?.itens?.length || 0), 0);
      return Math.max(storeCount, this.itensTabela.length);
    },
    totalValor() {
      return this.itensTabela.reduce((sum, item) => sum + ((item.precoComImposto || item.precoUnitario || item.precoUnitarioBase) * item.pedidoItemQuantidade), 0);
    },
    totalUnidades() {
      return this.itensTabela.reduce((sum, item) => sum + item.pedidoItemQuantidade, 0);
    },
    temMaisReferencias() {
      return this.referenciasPendentes.length > 0;
    },
    ...mapGetters('intensPedido', [
      'getEsMarcas',
      'getIsLoadingMarca',
      'getEsCategorias',
      'getIsLoadingCategoria',
      'getEsReferencias',
      'getIsLoadingReferencia',
      'getEsEmbalagens',
      'getIsLoadingEmbalagem',
      'getFormData',
      'getlistarTabelaItens',
    ]),
  },
  methods: {
    ...mapActions('intensPedido', [
      'itensMarcaPedido',
      'itensCategoriaPedido',
      'itensReferenciaPedido',
      'itensEmbalagemPedido',
      'itensdetalhes',
      'detalhesPedidoStored', // STORED PROCEDURE
      'setListTabelaItens',
      'setIsLoadingReferencia',
      'setIsLoadingReferenciaRestante',
      'setjsonTelaNovaPedido',
      'atualizarItemJsonTelaNovaPedido',
      'clearjsonTelaNovaPedido',
      'clearModalItens',
      'buscaCorGradeItens',
      'buscarReferenciaPorCodigo',
    ]),
    /**
     * Cada componente deve sobrescrever este hook retornando o id da tabela
     * de preço do seu contexto (novo pedido vs. pedido existente).
     */
    getTabelaPrecoId() {
      return null;
    },
    onTipoBuscaChange() {
      this.limparFiltros();
    },
    trimCodigoReferencia() {
      if (this.codigo_referencia_direto) {
        this.codigo_referencia_direto = this.codigo_referencia_direto.trim();
      }
    },
    trimBuscaReferencia() {
      if (this.busca_referencia) {
        this.busca_referencia = this.busca_referencia.trim();
      }
    },
    async buscarPorCodigoReferencia() {
      this.referenciasPendentes = [];
      this.codigo_referencia_direto = this.codigo_referencia_direto?.trim() || '';

      if (!this.codigo_referencia_direto) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Atenção',
            text: 'Digite o código da referência',
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        });
        return;
      }

      this.isLoadingAllData = true;
      await this.clearjsonTelaNovaPedido();

      try {
        const resultado = await this.buscarReferenciaPorCodigo({
          codReferencia: this.codigo_referencia_direto,
          tabelaPrecoId: this.getTabelaPrecoId(),
        });

        if (resultado.success && Array.isArray(resultado.data) && resultado.data.length > 0) {
          const referenciasUnicas = [];
          const combinacoesVistas = new Set();

           for (const referencia of resultado.data) {
             const itemId = referencia.item_id ?? referencia.itemId ?? referencia.CodItem;
             // The catalog contract is one row per SKU.  Do not synthesize an
             // id from a commercial family when an old tenant sends an
             // incomplete row; such a value is not a sellable SKU.
             if (itemId === null || itemId === undefined || String(itemId).trim() === '') continue;
             const chaveUnica = `sku-${itemId}`;

            if (!combinacoesVistas.has(chaveUnica)) {
              combinacoesVistas.add(chaveUnica);
              referenciasUnicas.push(referencia);
            }
          }

          if (referenciasUnicas.length === 0) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Informação',
                text: 'Nenhuma referência única encontrada',
                icon: 'InfoIcon',
                variant: 'info',
              },
            });
            return;
          }

          const referenciasParaProcessar = referenciasUnicas.map((ref) => ({
             CodReferencia: ref.CodReferencia ?? ref.familia_comercial_id,
             Referencia: ref.Referencia ?? ref.item_descricao ?? ref.itemDescricao ?? ref.Descricao,
             item_id: ref.item_id ?? ref.itemId ?? ref.CodItem,
             itemId: ref.item_id ?? ref.itemId ?? ref.CodItem,
             item_descricao: ref.item_descricao ?? ref.itemDescricao ?? ref.Descricao,
             itemDescricao: ref.item_descricao ?? ref.itemDescricao ?? ref.Descricao,
             Descricao: ref.Descricao ?? ref.item_descricao ?? ref.itemDescricao,
            item_codigo_origem: ref.item_codigo_origem,
            des_marca: ref.des_marca,
            cod_marca: ref.cod_marca,
            cod_categ: ref.cod_categ,
            cdn_cor: ref.cdn_cor,
            cdn_tamanho: ref.cdn_tamanho,
            cdn_embalagem: ref.cdn_embalagem,
            des_cor: ref.des_cor,
            des_tamanho: ref.des_tamanho,
            des_embalag: ref.des_embalag,
          }));

          this.selected_cod_marca = referenciasUnicas[0].cod_marca;
          this.selected_categoria = referenciasUnicas[0].cod_categ;

          // Processa em blocos (lazy) p/ não disparar centenas de detalhes de uma vez
          this.referenciasPendentes = referenciasParaProcessar;
          await this.processarBlocoReferencias();

           const mensagem = referenciasUnicas.length === 1
             ? `Item encontrado: ${referenciasUnicas[0].item_id ?? referenciasUnicas[0].itemId}`
             : `${referenciasUnicas.length} itens encontrados`;

          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Sucesso',
              text: mensagem,
              icon: 'CheckIcon',
              variant: 'success',
            },
          });
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Informação',
              text: resultado.message || 'Referência não encontrada',
              icon: 'InfoIcon',
              variant: 'info',
            },
          });
        }
      } catch (error) {
        console.error('Erro ao buscar por código de referência:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro',
            text: 'Erro ao buscar referência',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.isLoadingAllData = false;
        await this.setIsLoadingReferenciaRestante(false);
      }
    },
    montarShellReferencias(refs) {
      (refs || []).forEach((ref) => {
        const itemId = ref.item_id ?? ref.itemId ?? ref.CodItem;
        if (itemId === null || itemId === undefined || String(itemId).trim() === '') return;
        const itemDescricao = ref.item_descricao ?? ref.itemDescricao ?? ref.Descricao
          ?? ref.Referencia ?? '';
        this.setjsonTelaNovaPedido({
          item_id: itemId,
          itemId,
          item_codigo_origem: ref.item_codigo_origem || null,
          referencia: ref.CodReferencia ?? ref.familia_comercial_id ?? null,
          item_name: itemDescricao,
          item_descricao: itemDescricao,
          itemDescricao,
          Descricao: ref.Descricao ?? itemDescricao,
          item_marca: ref.des_marca || '',
          cod_marca: ref.cod_marca != null ? ref.cod_marca : this.selected_cod_marca,
          cod_categ: ref.cod_categ != null ? ref.cod_categ : this.selected_categoria,
          item_price: 0,
          item_price_base: 0,
          imagens: [],
          item_img_princial: null,
          embalagens: [],
          // Keep local lookup metadata available while the exact detail is
          // loading. Integrated rows simply leave these fields undefined.
          cdnCorUnico: ref.cdn_cor,
          desCorUnico: ref.des_cor,
          cdnTamanhoUnico: ref.cdn_tamanho,
          cdnEmbalagemUnico: ref.cdn_embalagem,
          desTamanhoUnico: ref.des_tamanho,
          desEmbalagemUnico: ref.des_embalag,
          _detalheCarregado: false,
          _carregandoDetalhe: false,
        });
      });
    },
    async processarBlocoReferencias() {
      if (!this.referenciasPendentes.length) return;
      const bloco = this.referenciasPendentes.slice(0, this.tamanhoBlocoReferencias);
      this.referenciasPendentes = this.referenciasPendentes.slice(this.tamanhoBlocoReferencias);
      this.montarShellReferencias(bloco);
      // Carrega automaticamente os detalhes (imagens, preço, embalagens/grade)
      // do bloco recém-montado, para que as IMAGENS apareçam já na listagem —
      // sem exigir o clique em "Selecionar". Processa em pequenos lotes p/ não
      // disparar todas as chamadas de detalhe (stored procedure + imposto) de
      // uma só vez.
      await this.preCarregarDetalhes();
    },
    // Dispara `carregarDetalheReferencia` (definido em cada componente-pai) para
    // todas as referências ainda sem detalhe carregado, em lotes com limite de
    // concorrência. Idempotente: o próprio `carregarDetalheReferencia` ignora
    // itens já carregados / em carregamento.
    async preCarregarDetalhes() {
      if (typeof this.carregarDetalheReferencia !== 'function') return;
      const itens = this.$store.getters['intensPedido/getjsonTelaNovaPedido'] || [];
      const pendentes = itens.filter((p) => p && !p._detalheCarregado && !p._carregandoDetalhe);
      if (!pendentes.length) return;
      const CONCORRENCIA = 3;
      for (let i = 0; i < pendentes.length; i += CONCORRENCIA) {
        const lote = pendentes.slice(i, i + CONCORRENCIA);
        // eslint-disable-next-line no-await-in-loop
        await Promise.all(
          lote.map((p) => Promise.resolve(this.carregarDetalheReferencia(p)).catch(() => {})),
        );
      }
    },
    // Configura o IntersectionObserver na sentinela do fim da lista. Ao chegar
    // perto do fim (rootMargin), busca o próximo bloco automaticamente, mantendo
    // o teto por bloco (não dispara tudo de uma vez) e respeitando o loading.
    _observarSentinelaReferencias() {
      this._desobservarSentinelaReferencias();
      const sentinela = this.$refs.sentinelaCarregarMais;
      if (!sentinela || typeof IntersectionObserver === 'undefined') return;
      // O corpo da modal (scrollable) é o container de rolagem; cai no viewport se não achar.
      const root = sentinela.closest('.modal-body') || null;
      this._referenciasObserver = new IntersectionObserver((entries) => {
        const entry = entries[0];
        if (entry && entry.isIntersecting && this.temMaisReferencias && !this.isLoadingAllData) {
          this.carregarMaisReferencias();
        }
      }, { root, rootMargin: '200px', threshold: 0 });
      this._referenciasObserver.observe(sentinela);
    },
    _desobservarSentinelaReferencias() {
      if (this._referenciasObserver) {
        this._referenciasObserver.disconnect();
        this._referenciasObserver = null;
      }
    },
    async carregarMaisReferencias() {
      if (this.isLoadingAllData || !this.referenciasPendentes.length) return;
      this.isLoadingAllData = true;
      await this.setIsLoadingReferenciaRestante(true);
      try {
        await this.processarBlocoReferencias();
      } catch (error) {
        console.error('Erro ao carregar mais referências:', error);
      } finally {
        this.isLoadingAllData = false;
        await this.setIsLoadingReferenciaRestante(false);
      }
    },
    async buscarReferencias() {
      this.referenciasPendentes = [];
      if (!this.selected_cod_marca || !this.selected_categoria) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Atenção',
            text: 'Selecione marca e categoria antes de buscar referências',
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        });
        return;
      }

      this.isLoadingAllData = true;
      await this.clearjsonTelaNovaPedido();

      try {
        await this.itensReferenciaPedido({
          selected_cod_marca: this.selected_cod_marca,
          selected_categoria: this.selected_categoria,
          precoId: this.getTabelaPrecoId(),
        });

        await this.setIsLoadingReferenciaRestante(true);

        this.selected_referencia = null;
        this.selected_embalagem = null;

        const referencias = this.$store.getters['intensPedido/getEsReferencias'];

        if (referencias && referencias.length > 0) {
          const referenciasParaProcessar = this.busca_referencia
            ? referencias.filter((ref) => {
              if (!ref.value) return false;
              const termo = this.busca_referencia.toLowerCase();
              const codRef = ref.value.codReferencia?.toString().toLowerCase() || '';
              const descRef = ref.value.referencia?.toLowerCase() || '';
              const itemId = ref.value.item_id?.toString().toLowerCase() || '';
              const itemCodigo = ref.value.item_codigo_origem?.toString().toLowerCase() || '';
              return codRef.includes(termo) || descRef.includes(termo)
                || itemId.includes(termo) || itemCodigo.includes(termo);
            })
            : referencias;

          if (referenciasParaProcessar.length === 0) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Informação',
                text: 'Nenhuma referência encontrada com esse termo',
                icon: 'InfoIcon',
                variant: 'info',
              },
            });
            this.isLoadingAllData = false;
            await this.setIsLoadingReferenciaRestante(false);
            return;
          }

          const referenciasFormatadas = referenciasParaProcessar.map((ref) => ({
             CodReferencia: ref.value?.codReferencia ?? ref.value?.familia_comercial_id,
             Referencia: ref.value?.referencia ?? ref.value?.item_descricao
               ?? ref.value?.itemDescricao ?? ref.value?.Descricao,
             item_id: ref.value?.item_id ?? ref.value?.itemId ?? ref.value?.CodItem,
             itemId: ref.value?.item_id ?? ref.value?.itemId ?? ref.value?.CodItem,
             item_descricao: ref.value?.item_descricao ?? ref.value?.itemDescricao
               ?? ref.value?.Descricao,
             itemDescricao: ref.value?.item_descricao ?? ref.value?.itemDescricao
               ?? ref.value?.Descricao,
             Descricao: ref.value?.Descricao ?? ref.value?.item_descricao
               ?? ref.value?.itemDescricao,
            item_codigo_origem: ref.value?.item_codigo_origem,
            des_marca: ref.value?.des_marca,
            cod_marca: ref.value?.cod_marca ?? this.selected_cod_marca,
            cod_categ: ref.value?.cod_categ ?? this.selected_categoria,
            cdn_cor: ref.value?.cdn_cor,
            cdn_tamanho: ref.value?.cdn_tamanho,
            cdn_embalagem: ref.value?.cdn_embalagem,
            des_cor: ref.value?.des_cor,
            des_tamanho: ref.value?.des_tamanho,
            des_embalag: ref.value?.des_embalag,
           })).filter((ref) => ref.item_id !== null && ref.item_id !== undefined
             && String(ref.item_id).trim() !== '');

          // Processa em blocos (lazy) p/ não disparar centenas de detalhes de uma vez
          this.referenciasPendentes = referenciasFormatadas;
          await this.processarBlocoReferencias();

          if (referenciasFormatadas.length === 0) {
            this.$toast({
              component: ToastificationContent,
              props: {
                title: 'Informação',
                text: 'Nenhum resultado encontrado para a busca realizada',
                icon: 'InfoIcon',
                variant: 'info',
              },
            });
          }
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Informação',
              text: 'Nenhuma referência encontrada para esta categoria',
              icon: 'InfoIcon',
              variant: 'info',
            },
          });
        }
      } catch (error) {
        console.error('Erro ao buscar referências:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro',
            text: 'Ocorreu um erro ao buscar referências',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.isLoadingAllData = false;
        await this.setIsLoadingReferenciaRestante(false);
      }
    },
    // Carrega as options dos filtros de atributo (língua, segmento, marca,
    // nível educacional, coleção) para a tabela de preço do pedido.
    async carregarFiltrosItens() {
      const precoId = this.getTabelaPrecoId();
      if (!precoId || this.isLoadingFiltros) return;
      this.isLoadingFiltros = true;
      try {
        const SLS_pedidosVendasService = (await import('@/services/Operacoes/sls_pedidoVendas.service')).default;
        const resp = await new SLS_pedidosVendasService().listarFiltrosItens(precoId);
        const data = resp?.data || {};
        this.filtrosOptions = {
          lingua: data.lingua || [],
          segmento: data.segmento || [],
          niveleducacional: data.niveleducacional || [],
          colecao: data.colecao || [],
          marcas: data.marcas || [],
        };
      } catch (e) {
        console.warn('Erro ao carregar filtros de itens:', e);
      } finally {
        this.isLoadingFiltros = false;
      }
    },
    temAlgumFiltro() {
      return !!(
        (this.filtroTexto && this.filtroTexto.trim())
        || this.filtroLingua || this.filtroSegmento || this.filtroMarca
        || this.filtroNivel || this.filtroColecao
      );
    },
    // Busca por filtros de atributo + texto opcional e alimenta a listagem
    // (mesmo pipeline em blocos da busca por código).
    async buscarPorFiltros() {
      this.referenciasPendentes = [];
      if (!this.temAlgumFiltro()) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Atenção',
            text: 'Escolha ao menos um filtro ou digite um termo de busca',
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        });
        return;
      }

      this.isLoadingAllData = true;
      await this.clearjsonTelaNovaPedido();

      try {
        const resultado = await this.$store.dispatch('intensPedido/buscarReferenciasComFiltros', {
          tabelaPrecoId: this.getTabelaPrecoId(),
          filtros: {
            texto: this.filtroTexto?.trim() || '',
            lingua: this.filtroLingua || '',
            segmento: this.filtroSegmento || '',
            niveleducacional: this.filtroNivel || '',
            colecao: this.filtroColecao || '',
            codMarca: this.filtroMarca || '',
          },
        });
        this.buscaFiltrosRealizada = true;

        if (resultado.success && Array.isArray(resultado.data) && resultado.data.length > 0) {
          const vistas = new Set();
          const referenciasParaProcessar = [];
          for (const ref of resultado.data) {
             const itemId = ref.item_id ?? ref.itemId ?? ref.CodItem;
             if (itemId === null || itemId === undefined || String(itemId).trim() === '') continue;
             const chave = `sku-${itemId}`;
            if (!vistas.has(chave)) {
              vistas.add(chave);
              referenciasParaProcessar.push({
                 CodReferencia: ref.CodReferencia ?? ref.familia_comercial_id,
                 Referencia: ref.Referencia ?? ref.item_descricao ?? ref.itemDescricao ?? ref.Descricao,
                 item_id: itemId,
                 itemId,
                 item_descricao: ref.item_descricao ?? ref.itemDescricao ?? ref.Descricao,
                 itemDescricao: ref.item_descricao ?? ref.itemDescricao ?? ref.Descricao,
                 Descricao: ref.Descricao ?? ref.item_descricao ?? ref.itemDescricao,
                item_codigo_origem: ref.item_codigo_origem,
                des_marca: ref.des_marca,
                cod_marca: ref.cod_marca,
                cod_categ: ref.cod_categ,
                cdn_cor: ref.cdn_cor,
                cdn_tamanho: ref.cdn_tamanho,
                cdn_embalagem: ref.cdn_embalagem,
                des_cor: ref.des_cor,
                des_tamanho: ref.des_tamanho,
                des_embalag: ref.des_embalag,
              });
            }
          }
          this.selected_cod_marca = referenciasParaProcessar[0].cod_marca;
          this.selected_categoria = referenciasParaProcessar[0].cod_categ;
          this.referenciasPendentes = referenciasParaProcessar;
          await this.processarBlocoReferencias();
        } else {
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Informação',
              text: resultado.message || 'Nenhum produto encontrado',
              icon: 'InfoIcon',
              variant: 'info',
            },
          });
        }
      } catch (error) {
        console.error('Erro ao buscar por filtros:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro',
            text: 'Erro ao buscar produtos',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.isLoadingAllData = false;
        await this.setIsLoadingReferenciaRestante(false);
      }
    },
    limparFiltrosAtributo() {
      this.filtroTexto = '';
      this.filtroLingua = null;
      this.filtroSegmento = null;
      this.filtroMarca = null;
      this.filtroNivel = null;
      this.filtroColecao = null;
      this.referenciasPendentes = [];
      this.buscaFiltrosRealizada = false;
      this.clearjsonTelaNovaPedido();
    },
    async limparFiltros() {
      this.selected_cod_marca = null;
      this.selected_categoria = null;
      this.selected_referencia = null;
      this.selected_embalagem = null;
      this.busca_referencia = '';
      this.codigo_referencia_direto = '';
      this.referenciasPendentes = [];
      this.clearjsonTelaNovaPedido();
      this.clearModalItens();
      this.novosItensAdicionados = false;

      if (this.tipoBusca === 'marca-categoria') {
        await this.getMarcaPedido();
      }
    },
    limparCamposDependendes() {
      this.selected_categoria = null;
      this.selected_referencia = null;
      this.selected_embalagem = null;
      this.busca_referencia = '';
      this.getCategoriaPedido();
    },
    limparReferencia() {
      this.busca_referencia = '';
      this.selected_referencia = null;
      this.selected_embalagem = null;
    },
    async getMarcaPedido() {
      await this.itensMarcaPedido(this.getTabelaPrecoId());
      this.selected_categoria = null;
      this.selected_referencia = null;
      this.selected_embalagem = null;
      this.busca_referencia = '';
    },
    async getCategoriaPedido() {
      await this.clearjsonTelaNovaPedido();
      if (this.selected_cod_marca) {
        await this.itensCategoriaPedido(this.selected_cod_marca);
        this.selected_categoria = null;
        this.selected_referencia = null;
        this.selected_embalagem = null;
      }
    },
    async getDetalhesItem(familiaComercialId, cod_marca, cod_categ, itemId = null) {
      try {
        const params = {
          familiaComercialId,
          precoId: this.getTabelaPrecoId(),
          codMarca: cod_marca || this.selected_cod_marca,
          codCateg: cod_categ || this.selected_categoria,
          itemId,
        };

        const response = await this.$store.dispatch('intensPedido/detalhesPedidoStored', params);

        if (!response) {
          console.warn('Nenhum detalhe encontrado para:', familiaComercialId);
          return {
            success: false,
            message: 'Nenhum detalhe encontrado',
          };
        }

        return {
          success: true,
          Result: response,
        };
      } catch (error) {
        console.error('Erro em getDetalhesItem:', error);
        return {
          success: false,
          error: error.message,
        };
      }
    },
    handleOk(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.addItens();
      this.handleSubmit();
      this.clearModalItens();
    },
    handleSubmit() {
      this.$nextTick(() => {
        this.$refs['my-modal'].toggle('#toggle-btn');
      });
    },
    handleRemoveItem(item) {
      console.log('REMOVENDO ITEM', item);
      this.itensTabela = this.itensTabela.filter((i) => i.itemId !== item.item_id && i.pedidoItemQuantidade !== item.valor);
      console.log('TABELA ATUAL', this.itensTabela);

      this.novosItensAdicionados = this.itensTabela.length > 0;
    },
  },
};
